/**
 * 時計コンポーネント
 * 現在時刻と天気情報を表示する
 */
import React, { useState, useEffect } from "react";
import axios from "axios";

// ルートパスからのインポート
import "~/css/Clock.css";
import { WeatherNameWithIcon } from "~/WeatherNames";
import { currentLocale } from "~/Commons";
import { useAtom } from "jotai/index";
import { languageAtom } from "~/Atom";
import { locationInfoAtom } from "~/AtomWithDynamoDB";

// 時間関連の定数
const timeConstants = {
  updateIntervalMs: 1000, // 時計更新間隔（ミリ秒）
  weatherUpdateIntervalMin: 15, // 天気情報更新間隔（分）
  secondsPerMinute: 60, // 1分あたりの秒数
  msPerSecond: 1000, // 1秒あたりのミリ秒数
  hoursPerDay: 24, // 1日あたりの時間数
  jstOffsetHours: 9, // 日本標準時のUTCからのオフセット
  msPerMinute: 60000, // 1分あたりのミリ秒数
};

// API関連の定数
const apiConfig = {
  openMeteoBaseUrl: "https://api.open-meteo.com/v1/forecast", // OpenMeteo API のベースURL
  timezone: "Asia/Tokyo", // タイムゾーン設定
  contentType: "application/json", // コンテンツタイプ
};

// パディング用定数
const padding = {
  size: 2, // パディングサイズ
  char: "0", // パディング文字
};

// 気象情報APIをコールするクライアント
const weatherClient = axios.create({
  headers: {
    "Content-Type": apiConfig.contentType,
  },
});

// 型定義
type WeatherInfo = {
  datetime: string;
  weatherCode: number;
};

type WeatherDataByTime = {
  datetime: string;
  weatherCode: number;
};

type WeatherHourlyData = {
  time: string[];
  weather_code: number[];
};

type WeatherApiResponse = {
  hourly: WeatherHourlyData;
};

/**
 * 時計コンポーネント
 * 現在時刻と天気情報を表示
 */
const Clock: React.FC = () => {
  const [time, setTime] = useState<Date>(new Date());
  const [blink, setBlink] = useState<boolean>(true);
  const [weatherInfo, setWeatherInfo] = useState<WeatherInfo | null>(null);
  const [language] = useAtom(languageAtom);
  const [location] = useAtom(locationInfoAtom);

  /**
   * 時計の時間を更新
   */
  useEffect(() => {
    const intervalId = setInterval(() => {
      const newTime = new Date();
      setTime(newTime);
      setBlink(true); // 秒が変わったタイミングでコロンを表示
    }, timeConstants.updateIntervalMs);

    return () => clearInterval(intervalId);
  }, []);

  /**
   * 気象情報を取得して更新
   */
  const updateWeatherInfo = async (): Promise<void> => {
    try {
      // APIコールし気象情報取得
      const res = await weatherClient.get<WeatherApiResponse>(
        `${apiConfig.openMeteoBaseUrl}?timezone=${apiConfig.timezone}&latitude=${location.lat}&longitude=${location.lon}&hourly=weather_code`
      );

      // 応答が予期した形であることを確認
      if (!res || !res.data || !res.data.hourly) {
        setWeatherInfo(null);
        return;
      }

      const weatherData = res.data.hourly;
      const weatherDataByTime: WeatherDataByTime[] = weatherData.time.map(
        (timeString: string, index: number) => {
          return {
            datetime: timeString,
            weatherCode: weatherData.weather_code[index],
          };
        }
      );

      // weatherDataByTimeから現在時刻の気象情報を取得
      const weather = weatherDataByTime.find((data: WeatherDataByTime) => {
        const date = new Date(data.datetime);
        return date.getHours() === time.getHours();
      });

      if (weather) {
        setWeatherInfo(weather);
      }
    } catch (error) {
      console.error("Failed to fetch weather data:", error);
      setWeatherInfo(null);
    }
  };

  /**
   * 定期的に気象情報を更新
   */
  useEffect(() => {
    // 初回レンダリング時に天気情報を取得
    updateWeatherInfo();

    // 定期的に天気情報を更新
    const weatherUpdateIntervalMs =
      timeConstants.weatherUpdateIntervalMin *
      timeConstants.secondsPerMinute *
      timeConstants.msPerSecond;

    const intervalId = setInterval(updateWeatherInfo, weatherUpdateIntervalMs);

    // クリーンアップ関数でインターバルをクリア
    return () => clearInterval(intervalId);
  }, []);

  /**
   * 時刻を表示用にフォーマット
   */
  const formatTime = (date: Date): React.JSX.Element => {
    const hours = String(date.getHours()).padStart(padding.size, padding.char);
    const minutes = String(date.getMinutes()).padStart(padding.size, padding.char);
    const seconds = String(date.getSeconds()).padStart(padding.size, padding.char);

    return (
      <>
        <span className="time-unit">{hours[0]}</span>
        <span className="time-unit">{hours[1]}</span>
        <span className="colon">{blink ? ":" : " "}</span>
        <span className="time-unit">{minutes[0]}</span>
        <span className="time-unit">{minutes[1]}</span>
        <span className="colon">{blink ? ":" : " "}</span>
        <span className="time-unit">{seconds[0]}</span>
        <span className="time-unit">{seconds[1]}</span>
      </>
    );
  };

  /**
   * 日本標準時の日付を取得
   */
  const getJSTDate = (date: Date): Date => {
    const utc = date.getTime() + date.getTimezoneOffset() * timeConstants.msPerSecond;
    const jstOffset =
      timeConstants.jstOffsetHours * timeConstants.secondsPerMinute * timeConstants.msPerSecond;
    return new Date(utc + jstOffset);
  };

  /**
   * 日付から曜日を取得
   */
  const getWeekday = (date: Date): string => {
    // 曜日リスト
    const weekdays =
      language === "ja-JP"
        ? ["日", "月", "火", "水", "木", "金", "土"]
        : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return weekdays[date.getDay()];
  };

  // JST時刻を取得
  const jstDate = getJSTDate(time);

  return (
    <>
      <div className="time-container">
        <div className="date">
          {jstDate.toLocaleDateString(currentLocale)} ({getWeekday(jstDate)}) {/*日本時刻に固定 */}
        </div>
        <div className="clock-container">
          <div className="time">{formatTime(jstDate)}</div>

          {weatherInfo ? <WeatherNameWithIcon weatherCode={weatherInfo.weatherCode} /> : "-"}
        </div>
      </div>
    </>
  );
};

export default Clock;
